import * as Sentry from "@sentry/react-native";
import { Platform } from "react-native";

import { sentryConfigForPlatform } from "@/config/public";
import { scrubSentryEvent } from "@/lib/redaction";
import { configureTelemetry } from "@/lib/telemetry";

/**
 * The Sentry adapter — the only file in the app that names the vendor.
 *
 * `lib/telemetry.ts` is the seam every report already goes through, and it
 * names no vendor on purpose. This file implements its `TelemetryClient`, so
 * swapping Sentry for something else is one file rather than every call site.
 *
 * ── What must stay true here ────────────────────────────────────────────────
 *
 * `docs/observability/SENTRY_DATA_POLICY.md` is the authority. Three things it
 * requires are enforced in this file and asserted by
 * `tests/telemetry/sentry-adapter.test.ts`:
 *
 * 1. **`beforeSend` is the scrubber.** It is the last point at which an event
 *    is still ours. Everything the SDK collects on its own — the authorization
 *    header, cookies, the raw body, the user's email and IP — arrives without
 *    anyone attaching it, so removing it is not a matter of being careful at
 *    call sites.
 * 2. **`sendDefaultPii` is false.** True is the SDK's way of opting into
 *    exactly the fields §2 of the policy forbids.
 * 3. **Session Replay, profiling and Logs stay off.** Policy §4: each needs its
 *    own privacy review before it is enabled. They are governed by sample-rate
 *    options here, not by a dashboard toggle, so the way they stay off is by
 *    those options being absent. Session Replay records the screen.
 *
 * ── Off by default ─────────────────────────────────────────────────────────
 *
 * `init()` returns without doing anything unless the environment's config says
 * both that reporting is enabled and that a DSN exists. Nothing is installed on
 * the seam in that case, so `captureException` stays the no-op it has been.
 */

/**
 * Starts crash reporting, if this environment is configured for it.
 *
 * Safe to call unconditionally and safe to call more than once — the second
 * call returns early because the seam already has a client.
 *
 * Returns whether reporting is now on, which the caller may log; it is a
 * boolean, not a DSN, so it discloses nothing.
 */
export function initTelemetry(): boolean {
  const config = sentryConfigForPlatform(Platform.OS === "web" ? "web" : "native");

  if (!config.enabled) return false;

  Sentry.init({
    dsn: config.dsn,
    // Which environment a report belongs to. One DSN per project, three
    // environments inside it — this is what separates them.
    environment: config.environment,

    // Which BUILD a report belongs to, and therefore which source map can
    // decode its stack. Spread rather than set, because the correct value on
    // native is *no value at all*: `nativeReleaseIntegration` ships by default
    // and reads `<bundleId>@<version>+<build>` from the native build — exactly
    // what `sentry-cli` stamps on the maps it uploads during that build — and
    // the SDK treats an `options.release` as stronger than the native one. So
    // setting it here would override the value the maps were uploaded under and
    // guarantee they never match. Web has no native build to read from, which
    // is why it is the surface that must supply one.
    ...(config.release ? { release: config.release } : {}),
    ...(config.dist ? { dist: config.dist } : {}),

    // Policy §2: the SDK must not add identity of its own accord.
    sendDefaultPii: false,

    // Performance tracing. 0 in every environment today; errors are unaffected.
    tracesSampleRate: config.tracesSampleRate,

    // Policy §4 — deliberately absent, and this is where "absent" is enforced:
    //   replaysSessionSampleRate  Session Replay records the screen.
    //   replaysOnErrorSampleRate  Same, triggered by an error.
    //   profilesSampleRate        Profiling.
    //   enableLogs                Sentry Logs.
    // Enabling any of these is a policy change, not a configuration change.

    // The last point at which the event is still ours.
    beforeSend: (event) => scrubSentryEvent(event),
  });

  configureTelemetry({
    send: (event) => {
      if (event.error) {
        // Already redacted by the seam. Reported as a message rather than an
        // Error because the original stack was flattened on the way through.
        Sentry.captureMessage(
          String(event.error.message ?? event.error.name ?? "error"),
          {
            level: event.level,
            extra: { ...event.context, error: event.error },
          },
        );
        return;
      }
      Sentry.captureMessage(event.message ?? "", {
        level: event.level,
        extra: event.context,
      });
    },
    setUser: (id) => {
      // An opaque id and nothing else. Policy §1 allows exactly this.
      Sentry.setUser(id === null ? null : { id });
    },
  });

  return true;
}
